import { Injectable } from "@angular/core";
import { Helper } from "@helpers/functions";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * Feature highlights names.
 */
export const FeatureHighlights = {
	PORTFOLIO_ALLOCATION: "portfolio-allocation"
};

/**
 * Feature highlights settings. Currently only view counter is used.
 */
export const FeatureHighlightsSettings: Record<string, FeatureHighlightSettings> = {
	[FeatureHighlights.PORTFOLIO_ALLOCATION]: {
		viewCounter: 4 // this is 3 views, because the counter is decremented before the check (onInit)
	}
};

@Injectable({
	providedIn: "root"
})
export class FeatureHighlightService {
	private readonly cookieName = "feature-highlight";

	constructor(private readonly launchDarklyService: LaunchDarklyService) {}

	/**
	 * Returns true if the feature should be highlighted.
	 * @param featureName
	 */
	public getShouldHighlight$(featureName: string): Observable<boolean> {
		return this.launchDarklyService
			.isActive$(LD_FLAG.NEW_FEATURE_HIGHLIGHT)
			.pipe(map((isActive) => isActive && this.getFeatureValue(featureName) > 0));
	}

	/**
	 * Decrement the feature views by 1.
	 * @param featureName
	 */
	public decrementHighlightCounter(featureName: string): void {
		const current = this.getFeatureValue(featureName);
		const newValue = Math.max(0, current - 1);
		this.setFeatureValue(featureName, newValue);
	}

	/**
	 * Returns current value from cookie, or value from settings if cookie is not set yet.
	 * @param featureName
	 */
	public getFeatureValue(featureName: string): number {
		const cookieValue = Helper.getCookie(this.getCookieName(featureName));
		if (cookieValue === "null") {
			// in case cookie is not found or the feature is not in settings, return 0
			if (!FeatureHighlightsSettings[featureName]) {
				return 0;
			}

			return FeatureHighlightsSettings[featureName].viewCounter;
		}

		return +cookieValue;
	}

	/**
	 * Sets the counter to 0 and effectively stops the highlight.
	 * @param featureName
	 */
	public stopHighlight(featureName: string): void {
		this.setFeatureValue(featureName, 0);
	}

	private setFeatureValue(featureName: string, value: number): void {
		Helper.setCookie(this.getCookieName(featureName), value.toString());
	}

	private getCookieName(featureName: string): string {
		return `${this.cookieName}-${featureName}`;
	}
}

interface FeatureHighlightSettings {
	viewCounter: number;
	clickCounter?: number; // unused, but kept for future use
}
